import { useContext } from 'react';
import { QueryClientContext, type QueryClient } from '@tanstack/react-query';
import type { Account } from '@/api/types';
import { queryKeys } from '@/api/queryKeys';
import { isConnectedUnsupported, isReauthRequired } from './failure';

/**
 * A mail action on a CONNECTED mailbox that failed for a reason about the
 * mailbox rather than the message, said with the mailbox's name (ADR 0071's
 * calendar/contacts pattern, applied to ADR 0072's mail actions).
 *
 * The server's own sentence for a rejected saved password is "…for this
 * mailbox". That is enough on a screen that shows one mailbox, and wrong
 * everywhere else: in the merged list, a snackbar, or the reading pane of a
 * connected mailbox, "this mailbox" reads as the one you signed in with.
 *
 * `didNotHappen` finishes the sentence ("the sender wasn’t blocked"), because
 * the useful half of a failure is what did not change.
 *
 * `undefined` for every other error: the caller's own copy applies.
 */
export function mailboxFailureSentence(
  error: unknown,
  email: string | undefined,
  didNotHappen: string,
): string | undefined {
  const who = email !== undefined && email !== '' ? email : 'This mailbox';
  if (isReauthRequired(error)) {
    return `${who} needs its password again, so ${didNotHappen}. Re-enter it in Settings → Accounts.`;
  }
  if (isConnectedUnsupported(error)) {
    const where = email !== undefined && email !== '' ? `connected mailboxes like ${email}` : 'connected mailboxes';
    return `This isn’t available for ${where} yet, so ${didNotHappen}.`;
  }
  return undefined;
}

/**
 * The query client when there is one. The reading pane's pieces are also
 * rendered bare in component tests, where a missing mailbox name is fine and
 * a throw is not.
 */
export function useOptionalQueryClient(): QueryClient | undefined {
  return useContext(QueryClientContext);
}

/** The address of a mailbox, from the account list already in the cache. */
export function cachedMailboxEmail(
  queryClient: QueryClient | undefined,
  accountId: string | undefined,
): string | undefined {
  if (queryClient === undefined || accountId === undefined || accountId === '') return undefined;
  return queryClient
    .getQueryData<Account[]>(queryKeys.accounts())
    ?.find((account) => account.id === accountId)?.email;
}

/**
 * After a reauth answer the server has flagged the mailbox, but sends no
 * event for it; re-reading the account list is what puts "Needs password" on
 * the switcher and the Accounts row, where the fix is.
 */
export function refreshAfterMailboxFailure(queryClient: QueryClient | undefined, error: unknown): void {
  if (queryClient === undefined || !isReauthRequired(error)) return;
  void queryClient.invalidateQueries({ queryKey: queryKeys.accounts() });
}

/**
 * Which mailbox a failed request was for. `ApiError` carries no URL, and a
 * bulk mutation fans out over several mailboxes, so the fan-out tags the error
 * it rethrows with the account it was sending to.
 */
const failedAccounts = new WeakMap<object, string>();

export function tagFailedAccount(error: unknown, accountId: string): void {
  if (typeof error === 'object' && error !== null && !failedAccounts.has(error)) {
    failedAccounts.set(error, accountId);
  }
}

export function failedAccountOf(error: unknown): string | undefined {
  return typeof error === 'object' && error !== null ? failedAccounts.get(error) : undefined;
}
